<!--

Copyright Paul B. Lucas (C) 2025   ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is rendered when invoked from a list of agreements with a linked agreement
number column, or from a review page.  This view renders all properties of the agreement and associated
employee.


Note: Developer pattern:   inline style is used to provide flex order attribute.  These are added to 
invoked CSS class

Revisions:
    02/25/2025      pblucas     initial creation

-->
<template> 
    <!-- container wraps the entire review display setting up flex-->
    <div id="_pdf_render" class="_master_container">
        <div id="_pdf_row1" class="_r1_container">
            <div class="_col_50" style="order:1;">
                <!-- row for agreement number and uuid-->
                <p class="_r1_p" style="order:1;">Agreement Number: <b>{{ agreement.number }}</b></p>
                <p class="_p_dimished" style="order:2;">UUID: {{ agreement.uuid ?? 'not set'}}</p>  
                <p class="_r1_p" style="Order: 3;" v-if="agreement['validity-period'].length > 0"> Loan term: <b>{{ agreement['validity-period'] }}</b> months</p>     
                <p class="_r1_p" style="Order: 4;" v-if="agreement['date-submitted'].length > 0"> Date submitted: {{ prettyDate(agreement['date-submitted']) }}</p>     
                <p class="_r1_pr" style="Order: 4;" v-if="agreement['date-submitted'].length == 0"> Agreement has not been submitted</p>     
            </div>
        </div>
        

        <div class="_r2_container" >
            <!-- flex sub container for image and contacts -->
            <div class="_col_50" style="order:1;">
                <!-- render image first the contacts list under image -->
                <div v-if="!config['suppress-photos']  && !employee['req-photo-suppress']">
                    <img class="_employee_image" :src="employee.image" >
                </div>
                <div v-else>
                    <img class="_employee_image" src="@/assets/defaultPhoto.png" >
                </div>
                <div class="_contacts_container">
                    <p class="_description_label" > Employee Contact Information</p>
                    <div class="_contacts_list">
                        <!-- list of contacts on file -->
                        <div class="_row" v-for="(contact,idx) in employee.contacts" :key="contact.uuid">
                            <div class="_scol_50">
                                <p class="contact_item_type"  style="order: 1;">{{contact.type}}</p>
                            </div>    
                            <div class="_scol_50">
                                <p class="contact_item_value" style="order: 2;">{{ contact.value }}</p>
                            </div>          
                        </div>
                    </div>
                </div>
            </div> 

            <!-- container for PID -->
            <div class="_col_50" style="order:2;">
            
                <!-- elements in normal ordering: employee PID -->
                <p class="_pid_name"> {{ employee['name-concat'] }}</p>
                <p class="_pid_title"> {{ employee.title }}</p>  
                <div class="_row">
                    <div class="_scol_35">
                        <p class="_pid_label">NetID:</p>
                    </div>
                    <div class="_scol_65">
                        <p class="_pid_p">{{ employee.netid }}</p>
                    </div>
                </div>   
                <div class="_row">
                    <div class="_scol_35">
                        <p class="_pid_label">UIN:</p>
                    </div>
                    <div class="_scol_65">
                        <p class="_pid_p">{{ employee.uin }}</p>
                    </div>
                </div> 
                <div class="_row">
                    <div class="_scol_35">
                        <p class="_pid_label">Home org:</p>
                    </div>
                    <div class="_scol_65">
                        <p class="_pid_p">{{ employee['home-org'] }}</p>
                    </div>
                </div> 
                <div class="_row">
                    <div class="_scol_35">
                        <p class="_pid_label">E-Class Code:</p>
                    </div>
                    <div class="_scol_65">
                        <p class="_pid_p">{{ employee['eclass-code'] }}</p>
                    </div>
                </div> 
                <div class="_row">
                    <div class="_scol_35">
                        <p class="_pid_label">College:</p>
                    </div>
                    <div class="_scol_65">
                        <p class="_pid_p">{{ employee.college }}</p>
                    </div>
                </div> 
                <div class="_row">
                    <div class="_scol_35">
                        <p class="_pid_label">College Code: </p>
                    </div>
                    <div class="_scol_65">
                        <p class="_pid_p">{{ employee['college-code'] }}</p>
                    </div>
                </div> 
                <div class="_row">
                    <div class="_scol_35">
                        <p class="_pid_label">Department: </p>
                    </div>
                    <div class="_scol_65">
                        <p class="_pid_p">{{ employee.department }}</p>
                    </div>
                </div> 
                <div class="_row">
                    <div class="_scol_35">
                        <p class="_pid_label">Division:</p>
                    </div>
                    <div class="_scol_65">
                        <p class="_pid_p">{{ employee.division }}</p>
                    </div>
                </div>   
                <div class="_row">
                    <div class="_scol_35">
                        <p class="_pid_label">Assets Location:</p>
                    </div>
                    <div class="_scol_65">
                        <p class="_pid_addr">{{ addressString }}</p>
                    </div>
                </div> 
                <div class="_row">
                    <div class="_scol_35">
                        <p class="_pid_label">Campus Mail:</p>
                    </div>
                    <div class="_scol_65">
                        <p class="_pid_p">{{ employee.mailcode }}</p>
                    </div>
                </div>       
            </div>
        </div>

        <!--  row 3 is a flex row for three columns: agreement type statements, work and workplace descriptions, and workplace qualifiers -->
        <div class="_r3_container">
 
            <div class="_col_50" style="order:1;">
                <div class="_text_description" style="order: 1;">
                    <!-- agreement data, notes & reason -->
                    <p class="_description_label">Agreement Notes</p>
                    <p class="_description">{{ agreement['agreement-notes'] }}</p>
                </div>
              
            </div>
            <div class="_col_50" style="order:2;">
                <div class="_text_description" style="order: 2;">
                    <!-- agreement data, work description and schedule -->
                    <p class="_description_label">Loan Reason</p>
                    <p class="_description">{{ agreement['loan-reason'] }}</p>
                </div>
            </div>
        </div>

        <!-- remote deployed assets -->
        <div class="_r4_container" style="order:4;">

            <p class="_description_label">Loaned Assets</p>
            <div class="_asset_box">
                <div v-if="viewableAssets.length != 0">
                    
                    <div class="_row">
                        <div class="_scol_20">
                            <p class="_p_item_title">Tag/Ident</p>
                        </div> 
                        <div class="_scol_20">
                            <p class="_p_item_title">Asset Type</p>
                        </div>
                        <div class="_scol_20">
                            <p class="_p_item_title">Serial No.</p>
                        </div>
                        <div class="_scol_40">
                            <p class="_p_item_title">Description</p>
                        </div>
                    </div>
                    <div class="_row" v-for="(_,idx) in viewableAssets">
                        <div class="_scol_20">
                            <p class="_p_asset_item">{{ viewableAssets[idx].tag }}</p>
                        </div>
                        <div class="_scol_20">
                            <p class="_p_asset_item">{{ prettyType(viewableAssets[idx]['asset-type']) }}</p>
                        </div>
                        <div class="_scol_20">
                            <p class="_p_asset_item">{{ viewableAssets[idx]['serial-no'] }}</p>
                        </div>
                        <div class="_scol_40">
                            <p class="_p_asset_item">{{ viewableAssets[idx]['description'] }}</p>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <p class="_assets_warning">No assets deployed</p>
                </div>
            </div>
        </div> 

        <!-- communications protocols -->
        <div class="_r5_container" style="order:5;">
 
            <div class="_col_50" style="order: 1;">                          
                <!-- employee certification and agreement -->
                <p class="_description_label">Employee Certifications<br/>
                    <span class="_p_dimished_grn">
                        Employee has agreed to terms and conditions as explained
                    </span>
                </p>
                <div class="_employee_certs_box">      
                    <div class="_row">
                        <div class="_scol_65">
                            <p class="_p_item_title">Agreement Policy Consent</p>
                        </div>
                        <div class="_scol_8">
                            <div class="_checks" v-if="agreement['policy-consent']"><img src="@/assets/check-green.png" width="10" /></div>
                            <div class="_checks" v-else><img src="@/assets/red-x.png" width="10" /></div> 
                        </div>
                    </div>   
                    <div class="_row">
                        <div class="_scol_65">
                            <p class="_p_item_title">Security Policy Consent</p>
                        </div>
                        <div class="_scol_8">
                            <div class="_checks" v-if="agreement['security-consent']"><img src="@/assets/check-green.png" width="10" /></div>
                            <div class="_checks" v-else><img src="@/assets/red-x.png" width="10" /></div> 
                        </div>
                    </div>   
                      
                </div>
                
            </div>
        </div>
        <!-- add lines to keep content out from under copyright notice -->
        <br />
        <br /> 

    </div>
 
</template>

<script>
    import { ref, computed }            from 'vue'
    import useRefreshAPI                from  '@/composables/useRefreshAPI'
    import usePermissionsAPI            from  '@/composables/usePermissionsAPI'
    import useDateHelpers               from '@/composables/useDateHelpers'
    import useEmployeesAPI              from '@/composables/useEmployeesAPI' 

    export default {
        components: {

        },
        props: [
            'agreement',
            'employee',
            'issuedAssets',
            'config',
            'claims',
            'gat'
        ],

        emits: [
            // cannot mutate agreement from within this component
            // emits send mutation out to parent
        ],
    
        setup(props)    {

            const { checkStoreForRefresh }  = useRefreshAPI()
            const { checkAdminGroups }      = usePermissionsAPI()

            // MARK:  refresh store from database if global browser refresh fubars the store
            checkStoreForRefresh()
            checkAdminGroups(props.claims)

            const addressString     =   computed( () => {
                let adrs = ""
                adrs     = (props?.agreement['asset-address1'])?(props.agreement['asset-address1']+'\n'):""
                adrs    += (props?.agreement['asset-address2'])?(props.agreement['asset-address2']+'\n'):""
                adrs    += (props?.agreement['asset-city'])    ?(props.agreement['asset-city']+', '):""
                adrs    += (props?.agreement['asset-state'])   ?(props.agreement['asset-state']+' '):""
                adrs    += (props?.agreement['asset-postcode'])?(props.agreement['asset-postcode']+'  '):""
                adrs    += (props?.agreement['asset-country']) ?(props.agreement['asset-country']+'\n'):""            
                return adrs
            })
   
            const viewableAssets   =   ref([])

            props.issuedAssets.forEach( (a) => {
                if(a.status == 'Active-Loaned')
                viewableAssets.value.push(a)
            })

            return {addressString, viewableAssets}
        },

        data() {
            return { }
        },

        methods:  {
            prettyDate(date)     {
                const { makePrettyDate }      =   useDateHelpers()
                return makePrettyDate(date)          
            },
            // Function: converts status enumeration to more readable 
            prettyType(s) {
                if (!s) return ""
                s = s.replaceAll('_',' ')
                let aW = s.toLowerCase().split(' ')
                for (let i = 0; i < aW.length; i++) {
                    aW[i] = aW[i].charAt(0).toUpperCase() + aW[i].slice(1);
                }
                return aW.join(' ')
            }

        },
        // hooks
        beforeMount() {
        },
        mounted()   {
        },        
        beforeUpdate() {
        }
    }
</script>

<style scoped>

    ._master_container   {
        width: 90%;
        height: 90%;
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        margin: 10px auto;
        padding: 0;
    }
  
    ._r1_p {
        /* specifies the CSS props for the Agreement number */
        font-size: 0.9em;
        text-align: left;
        margin: 0;
        padding: 2px;
    }
    ._r1_pr {
        /* specifies the CSS props for the Agreement number */
        color: #FF5F05;
        font-size: 0.9em;
        text-align: left;
        margin: 0;
        padding: 2px;
    }
    ._r1_p_status {
        /* specifies the CSS props for the Agreement status */
        font-size: 1.2em;
        color: #FF5F05;
        text-align: right;
        margin: 0;
        padding: 2px;
    }
    ._p_dimished {
        /* specifies the CSS props for the uuid information */
        text-align: left;
        font-size:  0.8em;
        color: black;
        margin: 0;
        padding: 2px;
    }
    ._p_dimished_grn {
        /* specifies the CSS props for the uuid information */
        text-align: left;
        font-size:  0.8em;
        color: #006230;
        margin: 0;
        padding: 2px;
    }
    /*specifies how to style the work arrangement */
    ._p_work {
        color: black;
        margin: 3px 0 0 20px;
        padding: 0;
        font-size: 0.9em;
        text-align: left;
        font-weight: bold;
    }
    ._employee_certs_box {
        /* shaded container for certs */
        margin: 0;
        padding: 0;
        border-style: solid;
        border-color: #13294B;
        border-width: 2px;
        background-color: #BFC9D9;
    }
    ._approvals_box {
        margin: 0;
        padding: 5px 0;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: #f9da98;
    }
    ._asset_box {
        width: 100%;
        min-height: 50px;
        background-color: #BFC9D9;
        border-style: solid;
        border-color: #13294B;
        border-width: 2px;
        margin: 0;
        padding: 0;
    }
    ._gold_box {
        width: 100%;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: #f9da98;
        font-size: 0.9em;
        color: black;
        margin: 0;
        padding: 0;
    }
    ._text_description   {
        width: 100%;
        min-height: 20%;
        margin:  0 5px 0 0;
        padding: 2px;
    }
    ._description_label {
        margin: 2px 0 4px 0;
        padding: 0;
        font-size: 0.95em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
    }
    ._description {
        min-height: 20px; 
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: #BFC9D9;
        font-size: 0.9em;
        font-weight: normal;
        text-align:justify;
        margin: 0 10px 0 0;
        padding: 5px 10px;
    }
    ._workplace_check_box {
        width: 100%;
        min-height: 20px;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: #f9da98;
        padding: 0;
    }
 
    /* schedule styling */
    ._schedule_container {
        width: 100%;
        min-height: 20%;
        margin:  0;
        padding: 5px 0 0 0;
    }
    ._schedule_box {
        width: 100%;
        min-height: 20px;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: #f9da98;
        padding: 0;
    }
    ._schedule_item {
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 0.9em;
        font-weight: normal;
        text-align: left;
        color: black;
        margin: 0;
        padding: 0 5px;
    }
    ._schedule_time {
        font-size: 0.9em;
        font-weight: normal;
        text-align: center;
        color: black;
        margin: 0;
        padding: 0 5px;
    }
    ._schedule_warning {
        text-align: center;
        color: red;
        margin: 0 auto;
        padding: 0;

    }
    ._qualifications_box {
        width: 100%;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: #f9da98;
        font-size: 0.8em;
        color: black;
        margin: 0;
        padding: 0;
    }

    /*  contact list styling */   
    ._contacts_container {
        width: 100%;
        margin: 5px 0 0 0;
        padding: 0;
    }
    ._contacts_list {
        width: 90%;
        margin: 0 5%;
        padding: 0;
    }
    .contact_item_type  {
        font-size: 0.7em;
        text-align: left;
        margin: 0 3px 0 0;
        padding: 0;
    }
    .contact_item_value {
        overflow: hidden;
        font-size: 0.8em;
        font-weight: bold;
        text-align: right;
        margin:  0;
        padding: 0 15px 0 0;
    }
   
    ._pid_p {
        width: 100%;
        font-size: 0.9em;
        font-weight: normal;
        text-align: left;
        margin: 0;
        padding: 2px 0;
    }
    ._pid_label {
        font-size: 0.9em;
        font-weight: normal;
        text-align: left;
        color: #13294B;
        margin: 0;
        padding: 2px 5px;
    }
    ._pid_name {
        margin: 0 auto;
        padding: 2px 0;
        font-size: 1.1em;
        font-weight: bold;
    }
    ._pid_title {
        margin: 0 auto 10px;
        padding: 2px 0;
        font-size: 0.9em;
        font-weight: normal;
    }

    ._pid_addr {
        width: 100%;
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        font-size: 0.9em;
        font-weight: normal;
        text-align: left;
        overflow: auto;
        margin: 2px 0 2px 0;
        padding: 2px 0;
    }

    ._radio_set {
        /* width relative to _work_type flex component */
        width: 100%;
        margin: 7px 0 0 0;
        padding: 0;
        text-align: left;
    }
    ._radio {
        /* the styling of the radio control <input> element itself  */
        display: inline;
        /* space label on right margin */
        margin: 1px 10px 1px 0;
        font-size: 0.6em;
        position: relative;
        top: 2px;
    }
    ._label_radio_1 {
        color: #575764;
        display: inline-block;
        margin: 1px 0 1px;
        padding: 0px;
        font-size: 0.6em;
        text-transform: uppercase;
        letter-spacing: 1px;
        font-weight: bold;
    }
  

    /* assets styling */
    ._p_asset_item {
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        text-align: left;
        margin: 0;
        padding: 2px 5px;
    }
   
    ._assets_warning {
        text-align: center;
        color: #FF5F05;
        margin: 5px auto;
        padding: 5px;
    }
    ._p_item_title   {
        width: 100%; 
        font-size: 0.9em;
        font-weight: normal;
        color: blue;
        text-align: left;
        margin: 0;
        padding: 2px 5px;
    }
    ._p_item_value   {
        width: 100%; 
        font-size: 0.9em;
        font-weight: normal;
        color: black;
        text-align: left;
        margin: 0;
        padding: 2px 5px;
    }
    ._checks {
        width: 5%;
        text-align: center;
        margin: 0 auto;
        padding: 0;
    }
    ._employee_image {
        /* employee image or default photo size to colum flex specified size */
        display: block;
        max-width: 60%;
        margin: 0 auto;
        padding: 0px;
    }
    
    /*  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
    /   RESPONSIVE DESIGN STYLING
    >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>/*
    /*  responsive design flex rows  5 major rows or 2 or 3 columns */
    ._r1_container, ._r2_container, ._r3_container,
    ._r5_container {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._r4_container {
        width: 100%;
        margin: 10px 0 0 0;
        padding: 0;
    }    
    
    /*  responive design columns - scol are subcolumns  */
    ._row:after {
        content: "";
        display: table;
        clear: both;
    }
    ._col_50 {
        /* float: left; */
        width: 50%;
        margin-top: 5px;
    }
    /*  sub columns - columns for items within a column*/
    ._scol_20 {
        float: left;
        width: 20%;
        margin-top: 5px;
    }
    ._scol_30 {
        float: left;
        width: 30%;
        margin-top: 5px;
    }
    ._scol_31 {
        float: left;
        width: 31%;
        margin-top: 5px;
    }
    ._scol_32 {
        float: left;
        width: 32%;
        margin-top: 5px;
    }
    ._scol_35 {
        float: left;
        width: 35%;
        margin-top: 1px;
    }
    ._scol_40 {
        float: left;
        width: 40%;
        margin-top: 5px;
    }
    ._scol_45 {
        float: left;
        width: 45%;
        margin-top: 5px;
    }
    ._scol_50 {
        float: left;
        width: 50%;
        margin-top: 5px;
    }
    ._scol_55 {
        float: left;
        width: 55%;
        margin-top: 5px;
    }
    ._scol_60 {
        float: left;
        width: 60%;
        margin-top: 5px;
    }
    ._scol_65 {
        float: left;
        width: 65%;
        margin-top: 1px;
    }
    ._scol_80 {
        float: left;
        width: 80%;
        margin-top: 5px;
    }
    ._scol_8 {
        float: left;
        width: 8%;
        margin-top: 5px;
    }

 
    @media screen and (max-width: 800px) {
        ._r2_container,
        ._r3_container {
            flex-wrap: wrap;  
        }
        
        ._col_50,
        ._col_32 {
            width: 100%;
            margin-top: 0;  
        }
        ._col_50 {
            margin: 0 10px 0 0;
        }
        ._scol_50 {
            width: 30%;
        }
        ._contacts_container {
            margin-bottom: 20px;
        }
        ._description {
            margin: 0;
        }
        ._employee_image {
            width:40%;
        }
    }
    @media screen and (max-width: 620px) {
        ._col_50 {
            width: 100%;
            margin-top: 0;  
        }
        ._pid_p {
            padding: 2px 5px;
        }
        .contact_item_type,
        .contact_item_value {
            width: 100%;
            text-align: left;
        }
        ._radio_set {
            margin: 2px 0 12px 0;
        }
        ._employee_image {
            width: 70%;
        }       
        ._scol_50  {
            width: 100%;
            margin-top: 0;
        }
        ._contacts_container {
            margin-bottom: 20px;
        }
        ._description {
            margin: 0;
            padding: 5px 10px;
        }
    }

</style>